// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\analytics\ReturnIndexPage\index.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Card, Select, DatePicker, Button, Table, Typography, Spin, message, 
    Row, Col, Tooltip, Statistic, Modal as AntdModal, List, Empty, Tag, Space,
    Collapse, Badge 
} from 'antd';
import { 
    SearchOutlined, InfoCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, 
    CheckCircleOutlined, IssuesCloseOutlined, TeamOutlined, BarChartOutlined, 
    FilterOutlined, ScheduleOutlined, DownloadOutlined, AuditOutlined, 
    UserOutlined, EnvironmentOutlined, RocketOutlined, StarFilled, CrownOutlined,
    WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { getAllDelegatesList } from '../../../services/delegateService'; 
import { getPhysicianReturnIndexForDelegate, exportPhysicianReturnIndex } from '../../../services/analyticsService'; 
import { fetchCycles } from '../../../redux/slices/planning/planningThunks';
import { returnIndexTableColumns } from './columns';
import { delegatePerformanceTableColumns } from './delegatePerformanceColumns';
import axiosInstance from "../../../services/axiosServicesVisit";
import Papa from 'papaparse';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const getCyclePeriod = (weeks) => { 
    if (!weeks || weeks.length === 0) return {};
    const sortedWeeks = [...weeks].sort((a,b) => (a.week_number ?? 0) - (b.week_number ?? 0));
    return { 
        overallStartDate: sortedWeeks[0]?.start_date, 
        overallEndDate: sortedWeeks[sortedWeeks.length - 1]?.end_date 
    };
};

const PhysicianReturnIndexPage = () => {
    const dispatch = useDispatch();
    
    // --- ÉTATS DÉLÉGUÉS ---
    const [allDelegates, setAllDelegates] = useState([]);
    const [loadingDelegates, setLoadingDelegates] = useState(false);
    const [selectedDelegateIdForMain, setSelectedDelegateIdForMain] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs('2026-01-01'), dayjs()]);
    const [selectedIsPartner, setSelectedIsPartner] = useState('all');

    // --- ÉTATS DONNÉES TABLEAU ---
    const [physicianReturnData, setPhysicianReturnData] = useState([]);
    const [loadingPhysicianReturnData, setLoadingPhysicianReturnData] = useState(false);
    const [errorPhysicianReturnData, setErrorPhysicianReturnData] = useState(null);
    
    // --- FILTRES LOCAUX TABLEAU ---
    const [selectedPhysicianFilterTable, setSelectedPhysicianFilterTable] = useState(null);
    const [selectedRegionFilterTable, setSelectedRegionFilterTable] = useState(null);
    const [selectedSpecialtyFilterTable, setSelectedSpecialtyFilterTable] = useState(null);

    // --- ÉTATS MODALE PERFORMANCE GLOBALE ---
    const [isDelegateModalVisible, setIsDelegateModalVisible] = useState(false);
    const [delegatePerformanceData, setDelegatePerformanceData] = useState([]);
    const [loadingDelegatePerformance, setLoadingDelegatePerformance] = useState(false);
    const [selectedDelegatesInModalFilter, setSelectedDelegatesInModalFilter] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [exportingModal, setExportingModal] = useState(false); 

    // --- ÉTATS MODALE PARCOURS MÉDECIN ---
    const [doctorModalVisible, setDoctorModalVisible] = useState(false);
    const [doctorDetailData, setDoctorDetailData] = useState([]);
    const [doctorLoading, setDoctorLoading] = useState(false);

    // --- REDUX ---
    const delegateCycles = useSelector(state => state.planning.cycles);
    const loadingCycles = useSelector(state => state.planning.loading.cycles);

    // Chargement initial des délégués
    useEffect(() => {
        setLoadingDelegates(true);
        getAllDelegatesList()
            .then(response => setAllDelegates(response?.data?.data || response?.data || []))
            .catch(() => message.error("Erreur chargement des délégués."))
            .finally(() => setLoadingDelegates(false));
    }, []);

    // Chargement des cycles quand un délégué est sélectionné
    useEffect(() => {
        if (selectedDelegateIdForMain) {
            dispatch(fetchCycles({ delegateId: selectedDelegateIdForMain }));
        }
    }, [dispatch, selectedDelegateIdForMain]);

    // --- LOGIQUE PARCOURS MÉDECIN ---
    const handleOpenDoctorHistory = async (personId) => {
        if (!personId) return;
        setDoctorModalVisible(true);
        setDoctorLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/delegate-stocks/analytics/doctor-full-details`, {
                params: { 
                    personId, 
                    startDate: '2020-01-01', 
                    endDate: dayjs().format('YYYY-MM-DD'), 
                    _t: Date.now() 
                }
            });
            setDoctorDetailData(res.data || []);
        } catch (e) {
            message.error("Erreur lors du chargement de l'historique");
        } finally {
            setDoctorLoading(false);
        }
    };

    // ⚡ EXPORT CSV DU PARCOURS AVEC LES NOTES / OBSERVATIONS ⚡
    const handleExportDoctorHistory = () => {
        if (doctorDetailData.length === 0) return;
        const docName = doctorDetailData[0].doctorName;
        const headers = ["Date", "Region", "Delegue Visiteur", "Type Visite", "Echantillons", "Produits", "Observations / Note"];
        const rows = doctorDetailData.map(item => {
            const isSupervisor = item.isSupervisor || 
                (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                (item.name && item.name.includes('[SUPERVISEUR]'));

            return [
                dayjs(item.reportDate).format('DD/MM/YYYY HH:mm'),
                item.regionName || "N/A",
                item.delegateName,
                isSupervisor ? "Superviseur" : "Délégué",
                item.samples?.map(s => `${s.name} (x${s.quantity})`).join(' | '),
                item.products?.join(' | '),
                `"${(item.note || item.comment || '').replace(/"/g, '""')}"`
            ];
        });
        const csvContent = [headers.join(";"), ...rows.map(e => e.join(";"))].join("\n");
        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Parcours_${docName.replace(/\s+/g, '_')}.csv`;
        link.click();
    };

    // --- LOGIQUE ANALYSE DÉLÉGUÉ ---
    const handleFetchDataForMainTable = useCallback(async () => {
        if (!selectedDelegateIdForMain) {
            message.warning("Veuillez sélectionner un délégué pour l'analyse.");
            return;
        }
        setLoadingPhysicianReturnData(true); 
        setErrorPhysicianReturnData(null); 
        
        const queryParams = { _t: Date.now() };

        if (dateRange && dateRange[0] && dateRange[1]) {
            queryParams.startDate = dateRange[0].format('YYYY-MM-DD');
            queryParams.endDate = dateRange[1].format('YYYY-MM-DD');
        }

        if (selectedIsPartner && selectedIsPartner !== 'all') {
            queryParams.isPartner = selectedIsPartner;
        }

        try {
            const response = await getPhysicianReturnIndexForDelegate(selectedDelegateIdForMain, queryParams);
            
            // FILTRE : On ignore les médecins avec Cible = 0 ET Visites = 0
            const activePhysicians = (response.data || []).filter(item => 
                (item.timesTargetedInCycles > 0) || (item.effectiveVisitsReported > 0)
            );

            const dataWithKeys = activePhysicians.map(item => ({
                ...item,
                uniqueKey: `${selectedDelegateIdForMain}-${item.physicianId}`
            }));
            
            setPhysicianReturnData(dataWithKeys);
            setSelectedPhysicianFilterTable(null);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Erreur récupération des données.";
            message.error(errorMsg); 
            setErrorPhysicianReturnData(errorMsg);
            setPhysicianReturnData([]);
        } finally {
            setLoadingPhysicianReturnData(false);
        }
    }, [selectedDelegateIdForMain, dateRange, selectedIsPartner]);
    
    const individualDelegateKpis = useMemo(() => {
        if (!selectedDelegateIdForMain || physicianReturnData.length === 0 || loadingPhysicianReturnData) return null;
        let physiciansAboveTarget = 0; 
        let physiciansTargetedInCycles = 0;
        physicianReturnData.forEach(item => {
            if (item.timesTargetedInCycles > 0) {
                physiciansTargetedInCycles++;
                if (item.returnIndex !== null && item.returnIndex >= 66) {
                    physiciansAboveTarget++;
                }
            }
        });
        const successRatePercent = physiciansTargetedInCycles > 0 ? parseFloat(((physiciansAboveTarget / physiciansTargetedInCycles) * 100).toFixed(1)) : 0;
        return { 
            physiciansAboveTarget, 
            physiciansBelowTarget: physiciansTargetedInCycles - physiciansAboveTarget, 
            physiciansTargetedInCycles, 
            successRatePercent 
        };
    }, [physicianReturnData, selectedDelegateIdForMain, loadingPhysicianReturnData]);

    const fetchDelegatePerformanceData = useCallback(async (delegateIdsToFetch) => {
        if (!delegateIdsToFetch || delegateIdsToFetch.length === 0) return;
        setLoadingDelegatePerformance(true);
        const queryParams = {};
        if (dateRange && dateRange[0] && dateRange[1]) {
            queryParams.startDate = dateRange[0].format('YYYY-MM-DD');
            queryParams.endDate = dateRange[1].format('YYYY-MM-DD');
        }

        const performancePromises = delegateIdsToFetch.map(delegateId =>
            getPhysicianReturnIndexForDelegate(delegateId, queryParams)
                .then(response => {
                    const delegateInfo = allDelegates.find(d => d.id === delegateId);
                    const firstName = delegateInfo?.firstname || delegateInfo?.firstName || '';
                    const lastName = delegateInfo?.lastname || delegateInfo?.lastName || '';
                    const delegateNameStr = `${firstName} ${lastName}`.trim() || `ID: ${delegateId}`;

                    const data = response.data || [];
                    let targeted = 0; 
                    let above66 = 0;
                    data.forEach(phys => {
                        if (phys.timesTargetedInCycles > 0) {
                            targeted++;
                            if (phys.returnIndex >= 66) above66++;
                        }
                    });
                    return { 
                        key: delegateId, 
                        delegateId, 
                        delegateName: delegateNameStr, 
                        physiciansTargeted: targeted, 
                        physiciansAbove66: above66, 
                        performanceRate: targeted > 0 ? parseFloat(((above66 / targeted) * 100).toFixed(1)) : null 
                    };
                }).catch(() => null)
        );
        const results = await Promise.all(performancePromises);
        setDelegatePerformanceData(results.filter(r => r !== null));
        setLoadingDelegatePerformance(false);
    }, [dateRange, allDelegates]);

    // --- LISTE DES MÉDECINS POUR LE SELECTEUR DE FILTRE ---
    const physicianOptions = useMemo(() => {
        return physicianReturnData
            .map(item => ({
                id: item.physicianId,
                name: `${item.physicianFirstName || ''} ${item.physicianLastName || ''}`.trim() || `ID: ${item.physicianId}`,
                isPartner: item.isPartner
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [physicianReturnData]);

    // Données filtrées affichées dans le tableau
    const displayedPhysicianData = useMemo(() => {
        return physicianReturnData.filter(item => {
            const mPhysician = selectedPhysicianFilterTable ? item.physicianId === selectedPhysicianFilterTable : true;
            const mRegion = selectedRegionFilterTable ? item.regionName === selectedRegionFilterTable : true;
            const mSpecialty = selectedSpecialtyFilterTable ? item.specialtyName === selectedSpecialtyFilterTable : true;
            return mPhysician && mRegion && mSpecialty;
        });
    }, [physicianReturnData, selectedPhysicianFilterTable, selectedRegionFilterTable, selectedSpecialtyFilterTable]);

    // --- EXPORTS ---
    const handleExport = async () => {
        if (!selectedDelegateIdForMain) return message.warning("Sélectionnez un délégué.");
        setExporting(true);
        try {
            const queryParams = dateRange ? { startDate: dateRange[0].format('YYYY-MM-DD'), endDate: dateRange[1].format('YYYY-MM-DD') } : {};
            if (selectedIsPartner !== 'all') queryParams.isPartner = selectedIsPartner;
            const response = await exportPhysicianReturnIndex(selectedDelegateIdForMain, queryParams);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `indice_retour_${selectedDelegateIdForMain}_${dayjs().format('YYYY-MM-DD')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success("Export réussi !");
        } catch { message.error("Erreur export."); }
        finally { setExporting(false); }
    };

    const handleExportModal = () => {
        const dataToExport = delegatePerformanceData.map(item => ({
            'Délégué': item.delegateName,
            'Médecins Ciblés': item.physiciansTargeted,
            'Indice ≥ 66%': item.physiciansAbove66,
            'Performance (%)': item.performanceRate ? `${item.performanceRate}%` : 'N/A'
        }));
        const csv = Papa.unparse(dataToExport, { delimiter: ';' });
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `performance_delegues_${dayjs().format('YYYY-MM-DD')}.csv`;
        link.click();
    };

    // --- CONFIGURATION COLONNES ---
    const columnsForMainTable = useMemo(() => returnIndexTableColumns(handleOpenDoctorHistory), []);
    const columnsForDelegatePerfModal = useMemo(() => delegatePerformanceTableColumns(), []);

    return (
        <div className="tabled">
            <Row gutter={[24, 24]}>
                {/* BLOC STATISTICS */}
                {selectedDelegateIdForMain && !loadingPhysicianReturnData && individualDelegateKpis && (
                    <>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Card bordered={false} className="criclebox h-full">
                                <Statistic title="Médecins Indice ≥ 66%" value={individualDelegateKpis.physiciansAboveTarget} valueStyle={{ color: '#3f8600' }} prefix={<CheckCircleOutlined />} />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Card bordered={false} className="criclebox h-full">
                                <Statistic title="Ciblés sous l'indice" value={individualDelegateKpis.physiciansBelowTarget} valueStyle={{ color: '#cf1322' }} prefix={<IssuesCloseOutlined />} />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Card bordered={false} className="criclebox h-full">
                                <Statistic title="Total Médecins Ciblés" value={individualDelegateKpis.physiciansTargetedInCycles} />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Card bordered={false} className="criclebox h-full">
                                <Statistic
                                    title="Taux Médecins Performants"
                                    value={individualDelegateKpis.successRatePercent}
                                    suffix="%"
                                    precision={1}
                                    valueStyle={{ color: individualDelegateKpis.successRatePercent >= 66 ? '#3f8600' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                    </>
                )}

                <Col xs={24} xl={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24 title_indice"
                        title={<Title level={4}><BarChartOutlined style={{marginRight: 8}} />Indice de Retour par Médecin</Title>}
                        extra={<Tooltip title="Calcul : (Visites Effectives / Cible Cycle) * 100"><InfoCircleOutlined style={{ color: '#1890ff' }}/></Tooltip>}
                    >
                        {/* FILTRES */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 24, padding: '0 20px' }} align="bottom">
                            <Col xs={24} sm={12} md={5}>
                                <Text strong>Délégué :</Text>
                                <Select
                                    showSearch placeholder="Sélectionner un délégué"
                                    loading={loadingDelegates} value={selectedDelegateIdForMain}
                                    onChange={(v) => {
                                        setSelectedDelegateIdForMain(v);
                                        setSelectedPhysicianFilterTable(null);
                                        setPhysicianReturnData([]);
                                    }}
                                    style={{ width: '100%', marginTop: 5 }}
                                    filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                                >
                                     {allDelegates.map(d => <Option key={d.id} value={d.id}>{`${d.firstname || ''} ${d.lastname || ''}`.trim()}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={5}>
                                <Text strong>Période d'analyse :</Text>
                                <RangePicker value={dateRange} onChange={setDateRange} style={{ width: '100%', marginTop: 5 }} />
                            </Col>
                            <Col xs={24} sm={12} md={3}>
                                <Text strong>Partenaire :</Text>
                                <Select 
                                    value={selectedIsPartner} 
                                    onChange={setSelectedIsPartner} 
                                    style={{ width: '100%', marginTop: 5 }}
                                >
                                    <Option value="all">Tous</Option>
                                    <Option value="true">⭐ Partenaires</Option>
                                    <Option value="false">Standards</Option>
                                </Select>
                            </Col>

                            {/* FILTRE MÉDECIN */}
                            <Col xs={24} sm={12} md={5}>
                                <Text strong>Médecin :</Text>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder={physicianReturnData.length === 0 ? "Analyser d'abord" : "Tous les médecins"}
                                    value={selectedPhysicianFilterTable}
                                    onChange={(val) => setSelectedPhysicianFilterTable(val)}
                                    disabled={physicianReturnData.length === 0}
                                    style={{ width: '100%', marginTop: 5 }}
                                    filterOption={(input, option) => 
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={physicianOptions.map(p => ({
                                        value: p.id,
                                        label: `${p.isPartner ? '⭐ ' : ''}${p.name}`
                                    }))}
                                />
                            </Col>

                            <Col xs={24} md={6}>
                                <Space wrap style={{ width: '100%' }}>
                                    <Button type="primary" icon={<SearchOutlined />} onClick={handleFetchDataForMainTable} loading={loadingPhysicianReturnData}>Analyser</Button>
                                    <Button icon={<TeamOutlined />} onClick={() => setIsDelegateModalVisible(true)}>Comparer</Button>
                                    <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={physicianReturnData.length === 0}>Exporter</Button>
                                </Space>
                            </Col>
                        </Row>

                        {/* CYCLES TAGS */}
                        {selectedDelegateIdForMain && (
                            <div style={{ padding: '0 20px 20px' }}>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 10 }}><ScheduleOutlined /> Cycles actifs :</Text>
                                <Row gutter={[8, 8]}>
                                    {delegateCycles
                                        .filter(cycle => {
                                            const { overallStartDate } = getCyclePeriod(cycle.weeks);
                                            return overallStartDate && dayjs(overallStartDate).year() === 2026;
                                        })
                                        .map(cycle => {
                                            const { overallStartDate, overallEndDate } = getCyclePeriod(cycle.weeks);
                                            const period = (overallStartDate && overallEndDate)
                                                ? `${dayjs(overallStartDate).format('DD/MM/YY')} - ${dayjs(overallEndDate).format('DD/MM/YY')}`
                                                : null;

                                            return (
                                                <Col key={cycle.id}>
                                                    <Tag 
                                                        color="#0e519b" 
                                                        style={{ 
                                                            padding: '5px 10px', 
                                                            fontSize: '13px',
                                                            borderRadius: '4px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start',
                                                            lineHeight: '1.4'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                                                            <Text style={{ color: 'white', fontWeight: 600 }}>
                                                                {cycle.name || `Cycle ${cycle.id}`}
                                                            </Text>
                                                            <Text style={{ color: '#d9d9d9', marginLeft: 12, fontSize: '12px' }}>
                                                                ({cycle.persons?.length || 0})
                                                            </Text>
                                                        </div>
                                                        {period && (
                                                            <Text style={{ color: '#ffffff', fontSize: '11px', fontWeight: 600 }}>
                                                                {period}
                                                            </Text>
                                                        )}
                                                    </Tag>
                                                </Col>
                                            );
                                        })}
                                </Row>
                            </div>
                        )}

                        <Table
                            columns={columnsForMainTable}
                            dataSource={displayedPhysicianData}
                            rowKey="uniqueKey"
                            loading={loadingPhysicianReturnData}
                            rowClassName={(record) => record.isPartner ? 'row-partner-gold' : ''}
                            pagination={{ pageSize: 50, showSizeChanger: true }}
                            scroll={{ x: 'max-content' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* MODALE PERFORMANCE DÉLÉGUÉS */}
            <AntdModal
                title={<Title level={5}><TeamOutlined /> Performance des Délégués</Title>}
                open={isDelegateModalVisible}
                onCancel={() => setIsDelegateModalVisible(false)}
                footer={[<Button key="close" onClick={() => setIsDelegateModalVisible(false)}>Fermer</Button>]}
                width="85%"
            >
                <Row gutter={[16,16]} style={{marginBottom: 20}}>
                    <Col span={18}>
                        <Select mode="multiple" placeholder="Filtrer Délégués" style={{ width: '100%' }} value={selectedDelegatesInModalFilter} onChange={setSelectedDelegatesInModalFilter}>
                            {allDelegates.map(d => <Option key={d.id} value={d.id}>{`${d.firstname} ${d.lastname}`}</Option>)}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Space>
                            <Button type="primary" onClick={() => fetchDelegatePerformanceData(selectedDelegatesInModalFilter.length > 0 ? selectedDelegatesInModalFilter : allDelegates.map(d => d.id))}>Calculer</Button>
                            <Button icon={<DownloadOutlined />} onClick={handleExportModal} disabled={delegatePerformanceData.length === 0}>Exporter</Button>
                        </Space>
                    </Col>
                </Row>
                <Table columns={columnsForDelegatePerfModal} dataSource={delegatePerformanceData} loading={loadingDelegatePerformance} rowKey="delegateId" />
            </AntdModal>

            {/* ========================================================================= */}
            {/* ⚡ MODALE PARCOURS DE VISITE DU MÉDECIN AVEC DISTINCTION SUPERVISEUR ⚡ */}
            {/* ========================================================================= */}
            <AntdModal 
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '95%', alignItems: 'center' }}>
                        <span><AuditOutlined /> Parcours de visite du Médecin</span>
                        <Button 
                            icon={<DownloadOutlined />} 
                            size="small" 
                            type="primary" 
                            onClick={handleExportDoctorHistory} 
                            disabled={doctorDetailData.length === 0}
                        >
                            Exporter Historique
                        </Button>
                    </div>
                } 
                open={doctorModalVisible} 
                onCancel={() => setDoctorModalVisible(false)} 
                footer={null} 
                width={1150}
            >
                <Spin spinning={doctorLoading}>
                    {/* 1. EN-TÊTE : FICHE RÉCAPITULATIVE DU MÉDECIN */}
                    {doctorDetailData.length > 0 && (
                        (() => {
                            const first = doctorDetailData[0];
                            const isPartner = first.isPartner === true || first.isPartner === 1 || first.isPartner === 'true';
                            const notes = first.partnerNotes;

                            // ⚡ 1. FILTRE : Uniquement les cycles oubliés à partir de l'année 2026 ⚡
                            const cyclesCoverage = (first.cyclesPlanningCoverage || []).map(cov => ({
                                ...cov,
                                unassignedCycles: (cov.unassignedCycles || []).filter(c => {
                                    if (!c.startDate) return false;
                                    return dayjs(c.startDate).year() >= 2026;
                                })
                            }));

                            const delegatesWithUnassigned = cyclesCoverage.filter(cov => (cov.unassignedCycles || []).length > 0);
                            const totalOublisCount = delegatesWithUnassigned.reduce((acc, curr) => acc + (curr.unassignedCycles?.length || 0), 0);

                            return (
                                <div>
                                    <div style={{ 
                                        marginBottom: 16, padding: '16px 20px', borderRadius: '10px',
                                        background: isPartner ? '#fffbe6' : '#f0f5ff', 
                                        border: isPartner ? '2px solid #fadb14' : '1px solid #adc6ff',
                                        boxShadow: isPartner ? '0 4px 12px rgba(250, 219, 20, 0.15)' : 'none'
                                    }}>
                                        <Row gutter={[16, 16]} align="middle">
                                            <Col span={16}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                                                    <UserOutlined style={{ fontSize: '28px', marginTop: '5px', color: isPartner ? '#856404' : '#1890ff' }} />
                                                    <div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                            <Title level={3} style={{ margin: 0, color: isPartner ? '#856404' : '#002766' }}>
                                                                {first.doctorName}
                                                            </Title>
                                                            {isPartner && (
                                                                <Tag color="gold" style={{ 
                                                                    fontSize: '13px', 
                                                                    fontWeight: '900', 
                                                                    border: '1px solid #d4b106', 
                                                                    borderRadius: '4px',
                                                                    padding: '2px 10px',
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                    <StarFilled style={{ marginRight: '6px' }} /> MÉDECIN PARTENAIRE
                                                                </Tag>
                                                            )}
                                                        </div>
                                                        <Space style={{ marginTop: 8 }} wrap size={8}>
                                                            <Tag color="blue" style={{ fontWeight: 'bold' }}>{first.specialtyName}</Tag>
                                                            <Text type="secondary"><EnvironmentOutlined /> {first.regionName || "N/A"}</Text>
                                                            {first.address && <Text type="secondary">({first.address})</Text>}
                                                        </Space>

                                                        {isPartner && notes && (
                                                            <div style={{ 
                                                                marginTop: '15px', 
                                                                padding: '10px 15px', 
                                                                background: 'rgba(255, 255, 255, 0.6)', 
                                                                borderRadius: '6px', 
                                                                borderLeft: '4px solid #fadb14' 
                                                            }}>
                                                                <Text strong style={{ color: '#856404', display: 'block', marginBottom: '3px' }}>
                                                                    Historique Partenariat :
                                                                </Text>
                                                                <Text style={{ color: '#856404', fontSize: '13px' }}>
                                                                    {notes}
                                                                </Text>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col span={8} style={{ textAlign: 'right', borderLeft: `2px solid ${isPartner ? '#fadb14' : '#adc6ff'}`, paddingLeft: '20px' }}>
                                                <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 8 }}>Délégués assignés :</Text>
                                                <Space wrap size={[4, 8]}>
                                                    {first.assignedDelegates?.length > 0 ? (
                                                        first.assignedDelegates.map((name, idx) => (
                                                            <Tag key={idx} color="cyan" icon={<RocketOutlined />} style={{ borderRadius: '10px' }}>{name.toUpperCase()}</Tag>
                                                        ))
                                                    ) : (
                                                        <Text type="secondary" italic style={{ fontSize: '11px' }}>Aucun délégué</Text>
                                                    )}
                                                </Space>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* ⚡ 2. ACCORDÉON DES OUBLIS DE PLANIFICATION PAR CYCLE (>= 2026) ⚡ */}
                                    {cyclesCoverage.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <Collapse
                                                defaultActiveKey={delegatesWithUnassigned.length > 0 ? ['audit_oubli'] : []}
                                                style={{ 
                                                    background: delegatesWithUnassigned.length > 0 ? '#fff2f0' : '#f6ffed', 
                                                    border: delegatesWithUnassigned.length > 0 ? '1px solid #ffa39e' : '1px solid #b7eb8f',
                                                    borderRadius: '8px'
                                                }}
                                                items={[
                                                    {
                                                        key: 'audit_oubli',
                                                        label: (
                                                            <Space>
                                                                {delegatesWithUnassigned.length > 0 ? (
                                                                    <>
                                                                        <WarningOutlined style={{ color: '#cf1322', fontSize: '16px' }} />
                                                                        <span style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '13px' }}>
                                                                            Alertes Oublis de Planification par Cycle
                                                                        </span>
                                                                        <Badge count={`${totalOublisCount} cycle(s) oublié(s)`} style={{ backgroundColor: '#cf1322' }} />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                                                        <span style={{ color: '#389e0d', fontWeight: 'bold', fontSize: '13px' }}>
                                                                            Planification Optimale : Ce médecin est planifié dans 100% des cycles
                                                                        </span>
                                                                        <Tag color="success" style={{ marginLeft: 8 }}>100% Couvert</Tag>
                                                                    </>
                                                                )}
                                                            </Space>
                                                        ),
                                                        children: (
                                                            <div>
                                                                {delegatesWithUnassigned.length > 0 ? (
                                                                    delegatesWithUnassigned.map((cov, idx) => {
                                                                        const unassignedList = cov.unassignedCycles || [];
                                                                        const assignedList = cov.assignedCycles || [];

                                                                        return (
                                                                            <div 
                                                                                key={idx} 
                                                                                style={{ 
                                                                                    padding: '10px 14px', 
                                                                                    background: '#ffffff', 
                                                                                    borderRadius: '6px', 
                                                                                    border: '1px solid #ffccc7',
                                                                                    marginBottom: idx !== delegatesWithUnassigned.length - 1 ? 8 : 0 
                                                                                }}
                                                                            >
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                                                    <Text strong style={{ color: '#1890ff', fontSize: '13px' }}>
                                                                                        👤 Délégué : {cov.delegateName}
                                                                                    </Text>
                                                                                    <Tag color="default" style={{ fontSize: '11px', color: '#595959' }}>
                                                                                        Planifié dans {assignedList.length} autre(s) cycle(s)
                                                                                    </Tag>
                                                                                </div>

                                                                                <div>
                                                                                    <Text strong style={{ fontSize: '12px', color: '#cf1322', display: 'block', marginBottom: 6 }}>
                                                                                        ⚠️ NON ASSIGNÉ dans ces cycles ({unassignedList.length}) :
                                                                                    </Text>
                                                                                    <Space wrap size={[6, 8]}>
                                                                                        {unassignedList.map((c, cIdx) => {
                                                                                            const periodStr = c.startDate && c.endDate 
                                                                                                ? `(${dayjs(c.startDate).format('DD/MM/YY')} au ${dayjs(c.endDate).format('DD/MM/YY')})`
                                                                                                : '';

                                                                                            return (
                                                                                                <Tag 
                                                                                                    key={cIdx} 
                                                                                                    color="error" 
                                                                                                    style={{ 
                                                                                                        fontWeight: 'bold', 
                                                                                                        fontSize: '11px', 
                                                                                                        padding: '3px 10px',
                                                                                                        borderRadius: '4px' 
                                                                                                    }}
                                                                                                >
                                                                                                    ⚠️ {c.cycleName} <span style={{ fontWeight: 'normal', opacity: 0.9, marginLeft: 4 }}>{periodStr}</span>
                                                                                                </Tag>
                                                                                            );
                                                                                        })}
                                                                                    </Space>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <Text style={{ color: '#389e0d', fontSize: '12px' }}>
                                                                        Tous les délégués assignés ont bien planifié ce médecin dans l'ensemble de leurs cycles. Aucun oubli détecté.
                                                                    </Text>
                                                                )}
                                                            </div>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })()
                    )}
                    
                    {/* 2. EN-TÊTE DU TABLEAU DES VISITES (AVEC COLONNE OBSERVATIONS) */}
                    <div style={{ 
                        background: '#f5f5f5', 
                        padding: '10px 15px', 
                        borderRadius: '6px', 
                        marginBottom: '10px', 
                        display: 'flex', 
                        fontWeight: 'bold',
                        fontSize: '12px',
                        border: '1px solid #e8e8e8'
                    }}>
                        <div style={{ width: '15%' }}>Date & Heure</div>
                        <div style={{ width: '20%' }}>Visiteur</div>
                        <div style={{ width: '22%' }}>Échantillons distribués</div>
                        <div style={{ width: '18%' }}>Produits présentés</div>
                        <div style={{ width: '25%' }}>Observations / Notes</div>
                    </div>
                    
                    {/* 3. LISTE DES VISITES AVEC IDENTIFICATION SUPERVISEUR */}
                    <List 
                        dataSource={doctorDetailData} 
                        locale={{ emptyText: "Aucune visite trouvée pour ce médecin." }}
                        renderItem={item => {
                            const isSupervisorVisit = item.isSupervisor || 
                                (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                                (item.name && (item.name.includes('[SUPERVISEUR]') || item.name.toLowerCase().includes('visite de contrôle')));

                            let displayNote = item.note || item.comment || '';
                            if (displayNote.includes('Observations :')) {
                                displayNote = displayNote.split('Observations :')[1].trim();
                            }

                            return (
                                <List.Item style={{ 
                                    padding: '12px 15px', 
                                    marginBottom: '8px',
                                    borderRadius: '6px',
                                    // ⚡ FOND VIOLET DOUX ET BORDURE VIOLETTE POUR LES SUPERVISEURS ⚡
                                    background: isSupervisorVisit ? '#fcf6ff' : '#ffffff', 
                                    border: isSupervisorVisit ? '1px solid #d3adf7' : '1px solid #f0f0f0',
                                    borderLeft: isSupervisorVisit ? '5px solid #722ed1' : '1px solid #f0f0f0',
                                    transition: 'all 0.2s'
                                }}>
                                    <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                        {/* Date & Heure */}
                                        <div style={{ width: '15%' }}>
                                            <Text strong>{dayjs(item.reportDate).format('DD/MM/YYYY')}</Text>
                                            <br/>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>{dayjs(item.reportDate).format('HH:mm')}</Text>
                                        </div>

                                        {/* Visiteur (Badge violet avec couronne 👑 si superviseur) */}
                                        <div style={{ width: '20%' }}>
                                            {isSupervisorVisit ? (
                                                <Tag color="purple" style={{ fontWeight: 'bold', padding: '3px 8px', borderRadius: '4px' }}>
                                                    <CrownOutlined style={{ marginRight: 5 }} /> {item.delegateName}
                                                </Tag>
                                            ) : (
                                                <Tag icon={<UserOutlined />} color="default">
                                                    {item.delegateName}
                                                </Tag>
                                            )}
                                        </div>

                                        {/* Échantillons */}
                                        <div style={{ width: '22%' }}>
                                            {item.samples && item.samples.length > 0 ? (
                                                item.samples.map((s, i) => (
                                                    <Tag 
                                                        key={i} 
                                                        color={s.isSpecific ? "error" : "processing"} 
                                                        style={{ marginBottom: '3px', fontSize: '11px' }}
                                                    >
                                                        {s.isSpecific && "[HORS CIBLE] "}{s.name} x{s.quantity}
                                                    </Tag>
                                                ))
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                                            )}
                                        </div>

                                        {/* Produits */}
                                        <div style={{ width: '18%' }}>
                                            {item.products && item.products.length > 0 ? (
                                                item.products.map((p, i) => (
                                                    <Tag key={i} color="orange" style={{ marginBottom: '3px', fontSize: '11px' }}>{p}</Tag>
                                                ))
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                                            )}
                                        </div>

                                        {/* ⚡ NOUVELLE COLONNE : OBSERVATIONS / NOTES ⚡ */}
                                        <div style={{ width: '25%' }}>
                                            {displayNote ? (
                                                <Tooltip title={item.note || displayNote}>
                                                    <div style={{ 
                                                        fontSize: '12px', 
                                                        color: isSupervisorVisit ? '#531dab' : '#262626', 
                                                        background: isSupervisorVisit ? '#f9f0ff' : '#f5f5f5',
                                                        padding: '5px 10px',
                                                        borderRadius: '4px',
                                                        border: isSupervisorVisit ? '1px dashed #d3adf7' : '1px solid #e8e8e8',
                                                        maxHeight: '48px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'pointer'
                                                    }}>
                                                        📝 {displayNote}
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                                            )}
                                        </div>
                                    </div>
                                </List.Item>
                            );
                        }} 
                    />
                    {doctorDetailData.length === 0 && !doctorLoading && <Empty description="Aucune visite trouvée pour ce médecin." />}
                </Spin>
            </AntdModal>

            <style>{`
                .row-partner-gold td {
                    background-color: #fffbe6 !important;
                }
                .row-partner-gold:hover td {
                    background-color: #fff1b8 !important;
                }
            `}</style>
        </div>
    );
};

export default PhysicianReturnIndexPage;